import { hashKey, type QueryClient, type QueryKey } from "@tanstack/react-query";
import { queryKeys } from "./query-keys";

type TenantScope = keyof typeof queryKeys.tenant extends infer K ? Exclude<K, "all"> : never;

/** The ONE invalidation helper: mutations name the scopes they touched, never raw keys (rules/01 §3). */
export async function invalidateTenantScopes(
  queryClient: QueryClient,
  tenantId: string,
  scopes: readonly TenantScope[],
): Promise<void> {
  await Promise.all(
    scopes.map((scope) => queryClient.invalidateQueries({ queryKey: queryKeys.tenant[scope].all(tenantId) })),
  );
}

/**
 * The same helper for a soft delete made from the record's own page (QA VI-2). The record now answers
 * 404 while its page is still mounted, waiting for the dialog to navigate away:
 *
 * - invalidating its detail query refetches it and the page draws "Not found" before the navigation;
 * - removing it from the cache is no better: the page's next render (the dialog closing) rebuilds the
 *   query and fetches it again.
 *
 * So each `gone` key is marked stale with NO refetch (the page keeps its last reading until it
 * unmounts; a later visit refetches on mount and reads the 404), and the rest of each scope is
 * invalidated as usual. Nothing is awaited, so the caller's navigation is not held behind the refetch.
 */
export function invalidateAfterSoftDelete(
  queryClient: QueryClient,
  tenantId: string,
  scopes: readonly TenantScope[],
  gone: readonly QueryKey[],
): void {
  const goneHashes = new Set(gone.map((key) => hashKey(key)));
  for (const queryKey of gone)
    void queryClient.invalidateQueries({ queryKey, exact: true, refetchType: "none" });
  for (const scope of scopes) {
    void queryClient.invalidateQueries({
      queryKey: queryKeys.tenant[scope].all(tenantId),
      predicate: (query) => !goneHashes.has(query.queryHash),
    });
  }
}

/** An infinite query the reader has paged back through: more than the head is in the cache. */
function pagedBeyondHead(data: unknown): boolean {
  const pages = (data as { pages?: unknown } | undefined)?.pages;
  return Array.isArray(pages) && pages.length > 1;
}

/**
 * The same helper for the live stream (M8): key prefixes in, one invalidation per matching cached
 * query out. The one difference is deliberate — a paged history (the event log twenty pages deep,
 * a delivery log the reader has scrolled) is marked stale but NOT replayed, because refetching
 * every loaded page on every burst would be twenty requests a second on a busy terminal and would
 * move rows under the reader's cursor. Those lists catch up through the "N new events" pill and
 * the next mount; a list still showing its head refetches at once, which is what "live" means.
 */
export async function invalidateLiveKeys(
  queryClient: QueryClient,
  keys: readonly (readonly unknown[])[],
): Promise<void> {
  const cache = queryClient.getQueryCache();
  // Keyed by queryHash so two prefixes naming the same query invalidate it once.
  const targets = new Map(
    keys.flatMap((queryKey) => cache.findAll({ queryKey }).map((query) => [query.queryHash, query] as const)),
  );
  await Promise.all(
    [...targets.values()].map((query) => {
      const { queryKey } = query;
      return pagedBeyondHead(query.state.data)
        ? queryClient.invalidateQueries({ queryKey, exact: true, refetchType: "none" })
        : queryClient.invalidateQueries({ queryKey, exact: true });
    }),
  );
}
